// ─── Auth Service ────────────────────────────────────────────────────────────
// Thin wrapper around Supabase Auth.
// All components should use these functions rather than calling supabase.auth directly.

import { supabase } from "./supabaseClient";
import type { Session } from "@supabase/supabase-js";

// ─── Types ───────────────────────────────────────────────────────────────────

export type { Session, User } from "@supabase/supabase-js";

// ─── Sign Up (email + password) ──────────────────────────────────────────────

export async function signUp(
  email: string,
  password: string
): Promise<{ error: string | null; needsConfirmation: boolean }> {
  const { data, error } = await supabase.auth.signUp({ email, password });
  return { error: error?.message ?? null, needsConfirmation: !error && !data.session };
}

// ─── Sign In (email + password) ──────────────────────────────────────────────

export async function signIn(
  email: string,
  password: string
): Promise<{ error: string | null }> {

  const { error } = await supabase.auth.signInWithPassword({ email, password });
  return { error: error?.message ?? null };
}

// ─── Sign In with Google OAuth ────────────────────────────────────────────────

export async function signInWithGoogle(): Promise<{ error: string | null }> {

  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: globalThis.location.origin },
  });
  return { error: error?.message ?? null };
}

// ─── Sign In with Spotify OAuth ───────────────────────────────────────────────

export async function signInWithSpotify(): Promise<{ error: string | null }> {

  const { error } = await supabase.auth.signInWithOAuth({
    provider: "spotify",
    options: { redirectTo: globalThis.location.origin },
  });
  return { error: error?.message ?? null };
}

// ─── Sign Out ─────────────────────────────────────────────────────────────────

export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
}

// ─── Get current session ──────────────────────────────────────────────────────

export async function getSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

// ─── Subscribe to auth state changes ─────────────────────────────────────────
// Returns an unsubscribe function. Call it in useEffect cleanup.

export function onAuthStateChange(
  callback: (session: Session | null) => void
): () => void {

  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(session);
  });

  return () => subscription.unsubscribe();
}
